import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import apiClient from '../requests/api/apiClient';
import { HttpResponse, IrrigationCommandResponse } from '../requests/api/apiTypes';
import { FetchError } from '../views/types';
import { StartIrrigationInput } from '../views/ClientDetail/Riego/irrigationControl.types';

type CommandRequest = () => Promise<HttpResponse<IrrigationCommandResponse>>;

export interface UseIrrigationControl {
  isSending: boolean;
  start: (input: StartIrrigationInput) => Promise<boolean>;
  stop: (irrigationId: number, wiseconnZoneId?: number) => Promise<boolean>;
  activate: (irrigationId: number) => Promise<boolean>;
  remove: (irrigationId: number, wiseconnZoneId?: number) => Promise<boolean>;
}

const errorMessage = (err: unknown): string => {
  const e = err as Partial<FetchError> & { message?: string };
  return e?.error?.message || e?.message || 'Error al ejecutar el comando de riego';
};

const useIrrigationControl = (
  farmId: string | undefined,
  onSuccess?: () => void,
): UseIrrigationControl => {
  const { enqueueSnackbar } = useSnackbar();
  const [isSending, setIsSending] = useState(false);

  const run = useCallback(async (
    request: CommandRequest,
    successText: string,
  ): Promise<boolean> => {
    if (!farmId) return false;
    setIsSending(true);
    try {
      const { data } = await request();
      const dryRun = Boolean(data?.command?.dryRun);
      enqueueSnackbar(dryRun ? `${successText} (simulado, dry-run)` : successText, {
        variant: dryRun ? 'info' : 'success',
      });
      onSuccess?.();
      return true;
    } catch (err) {
      enqueueSnackbar(errorMessage(err), { variant: 'error' });
      return false;
    } finally {
      setIsSending(false);
    }
  }, [farmId, enqueueSnackbar, onSuccess]);

  const start = useCallback((input: StartIrrigationInput) => run(
    () => apiClient.irrigation.start(farmId as string, input),
    input.directSchedule ? 'Riego programado' : 'Propuesta de riego enviada',
  ), [run, farmId]);

  const stop = useCallback((irrigationId: number, wiseconnZoneId?: number) => run(
    () => apiClient.irrigation.stop(farmId as string, irrigationId, wiseconnZoneId),
    'Riego detenido',
  ), [run, farmId]);

  const activate = useCallback((irrigationId: number) => run(
    () => apiClient.irrigation.activate(farmId as string, irrigationId),
    'Riego reactivado',
  ), [run, farmId]);

  const remove = useCallback((irrigationId: number, wiseconnZoneId?: number) => run(
    () => apiClient.irrigation.remove(farmId as string, irrigationId, wiseconnZoneId),
    'Riego eliminado',
  ), [run, farmId]);

  return {
    isSending, start, stop, activate, remove,
  };
};

export default useIrrigationControl;
